import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Hash, Plus, Download, FileText, CheckCircle2, Clock, AlertCircle, X, Send,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const emptyForm = { rfc: "", permit_number: "", business_name: "", notes: "" };

export function ClientSGMTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sgm-requests");
      setRequests(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/sgm-requests", form);
      toast.success("Solicitud enviada. Le notificaremos al aprobar.");
      setOpen(false);
      setForm(emptyForm);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const download = async (r) => {
    try {
      const res = await api.get(`/sgm-requests/${r.id}/certificate`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `sgm-web-${r.folio}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Error al descargar"); }
  };

  const stats = {
    total: requests.length,
    approved: requests.filter((r) => r.status === "approved").length,
    pending: requests.filter((r) => r.status === "pending").length,
  };

  const statusBadge = (s) => {
    if (s === "approved") return { c: "text-emerald-400 border-emerald-400/40 bg-emerald-400/5", l: "Aprobado", I: CheckCircle2 };
    if (s === "rejected") return { c: "text-red-400 border-red-400/40 bg-red-400/5", l: "Rechazado", I: AlertCircle };
    return { c: "text-amber-400 border-amber-400/40 bg-amber-400/5", l: "Pendiente", I: Clock };
  };

  return (
    <section data-testid="client-sgm-tab" className="mt-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="overline mb-2">// Producto SGM Web</div>
          <h2 className="font-heading font-bold uppercase text-2xl sm:text-3xl tracking-tight">
            <Hash className="inline w-6 h-6 text-[#F59E0B] mr-2 -mt-1" />
            Generación de Hash SHA-256
          </h2>
          <p className="text-[#A1A1AA] text-sm mt-2 max-w-2xl">
            Envíe su RFC y número de permiso. Nuestro equipo generará el hash criptográfico y le entregará el certificado en PDF.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary" data-testid="sgm-new-request-btn">
              <Plus className="w-4 h-4" /> Nueva Solicitud
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-lg rounded-none p-0 flex flex-col">
            <DialogHeader className="px-6 py-5 border-b border-[#2E2E33]">
              <DialogTitle className="font-heading font-black uppercase tracking-tight">Solicitar Hash SGM Web</DialogTitle>
              <DialogDescription className="text-[#A1A1AA]">
                Proporcione los datos. Será procesada y aprobada por nuestro equipo.
              </DialogDescription>
            </DialogHeader>
            <form id="sgm-form" onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <div>
                <label className="overline block mb-2">RFC *</label>
                <input required maxLength={20} minLength={10} className="input-base uppercase" value={form.rfc}
                  onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                  placeholder="XAXX010101000" data-testid="sgm-rfc" />
              </div>
              <div>
                <label className="overline block mb-2">Número de Permiso *</label>
                <input required className="input-base uppercase" value={form.permit_number}
                  onChange={(e) => setForm({ ...form, permit_number: e.target.value.toUpperCase() })}
                  placeholder="PLT-CRE-2024-XXXX" data-testid="sgm-permit" />
              </div>
              <div>
                <label className="overline block mb-2">Razón Social (opcional)</label>
                <input className="input-base" value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  data-testid="sgm-business" />
              </div>
              <div>
                <label className="overline block mb-2">Notas (opcional)</label>
                <textarea rows={3} className="input-base resize-none" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="sgm-notes" />
              </div>
            </form>
            <DialogFooter className="px-6 py-4 border-t border-[#2E2E33] bg-[#141418] gap-2">
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
                <X className="w-4 h-4" /> Cancelar
              </button>
              <button type="submit" form="sgm-form" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="sgm-submit-btn">
                {saving ? "Enviando..." : "Enviar Solicitud"} <Send className="w-4 h-4" />
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* SGM KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#2E2E33] border border-[#2E2E33] mb-8">
        <div className="bg-[#1F1F25] p-4 sm:p-6">
          <div className="font-mono text-[10px] sm:text-xs text-[#71717A] uppercase tracking-widest mb-1 sm:mb-2">// Servicios Contratados</div>
          <div className="font-heading font-black text-3xl sm:text-4xl text-white">{stats.total}</div>
        </div>
        <div className="bg-[#1F1F25] p-4 sm:p-6">
          <div className="font-mono text-[10px] sm:text-xs text-[#71717A] uppercase tracking-widest mb-1 sm:mb-2">// Aprobados</div>
          <div className="font-heading font-black text-3xl sm:text-4xl text-emerald-400">{stats.approved}</div>
        </div>
        <div className="bg-[#1F1F25] p-4 sm:p-6">
          <div className="font-mono text-[10px] sm:text-xs text-[#71717A] uppercase tracking-widest mb-1 sm:mb-2">// Pendientes</div>
          <div className="font-heading font-black text-3xl sm:text-4xl text-[#F59E0B]">{stats.pending}</div>
        </div>
      </div>

      <div className="border border-[#2E2E33] bg-[#1F1F25]" data-testid="sgm-requests-list">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#2E2E33] font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
          <div className="col-span-2">Folio</div>
          <div className="col-span-2">RFC</div>
          <div className="col-span-2">Permiso</div>
          <div className="col-span-2">Hash</div>
          <div className="col-span-2">Estado</div>
          <div className="col-span-2 text-right">Acción</div>
        </div>
        {loading ? (
          <div className="p-12 text-center text-[#A1A1AA] font-mono text-sm">Cargando solicitudes...</div>
        ) : requests.length === 0 ? (
          <div className="p-16 text-center">
            <Hash className="w-12 h-12 text-[#2E2E33] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">Aún no tiene solicitudes SGM Web. Crea la primera.</p>
          </div>
        ) : (
          requests.map((r) => {
            const sb = statusBadge(r.status);
            const SI = sb.I;
            return (
              <div key={r.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 sm:px-6 py-4 border-b border-[#2E2E33] last:border-b-0 hover:bg-[#26262C] items-center" data-testid={`sgm-row-${r.id}`}>
                <div className="md:col-span-2 font-mono text-sm text-white">{r.folio}</div>
                <div className="md:col-span-2 font-mono text-xs text-[#A1A1AA]">{r.rfc}</div>
                <div className="md:col-span-2 font-mono text-xs text-[#A1A1AA] break-all">{r.permit_number}</div>
                <div className="md:col-span-2 font-mono text-[10px] text-[#F59E0B] break-all">
                  {r.hash_sha256 ? `${r.hash_sha256.slice(0, 16)}...` : "—"}
                </div>
                <div className="md:col-span-2">
                  <span className={`inline-flex items-center gap-1 border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${sb.c}`}>
                    <SI className="w-3 h-3" /> {sb.l}
                  </span>
                  {r.status === "rejected" && r.rejection_reason && (
                    <div className="text-[10px] text-red-300 mt-1">{r.rejection_reason}</div>
                  )}
                </div>
                <div className="md:col-span-2 flex md:justify-end">
                  {r.status === "approved" ? (
                    <button onClick={() => download(r)} className="btn-primary py-2 px-3 text-[10px]" data-testid={`sgm-download-${r.id}`}>
                      <Download className="w-3.5 h-3.5" /> Certificado PDF
                    </button>
                  ) : (
                    <span className="font-mono text-[10px] text-[#71717A] uppercase tracking-widest">// {r.status}</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
